/**
 * Locations Management React Query Hooks
 * 
 * Provides React Query hooks for warehouse location operations including:
 * - Fetching locations with filtering
 * - Creating, updating, and deleting locations
 * - Importing locations from CSV
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useLocations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches locations with optional filtering
 * 
 * Supports comprehensive filtering options for warehouse locations including:
 * - Warehouse filtering
 * - Zone filtering (by ID or name)
 * - Search functionality
 * - Pagination
 * - Active status filtering
 * 
 * @param {Object} filters - Query parameters for filtering locations
 * @param {string} [filters.warehouse_id] - Filter by warehouse ID
 * @param {string} [filters.type] - Filter by location type
 * @param {string} [filters.zone_id] - Filter by zone ID
 * @param {string} [filters.zone] - Filter by zone name
 * @param {boolean} [filters.is_active] - Filter by active status
 * @param {string} [filters.search] - Search by name or code
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.page_size] - Items per page
 * @param {number} [filters.limit] - Limit number of results (alias for page_size)
 * @returns {Object} React Query result with locations data
 * @property {Object} data - Locations response object
 * @property {Array} data.locations - Array of location records
 * @property {Array} data.items - Alias for locations array (for compatibility)
 * @property {number} data.total - Total number of locations
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useLocations = (filters = {}) => {
  return useQuery({
    queryKey: ['locations', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
        if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
        if (filters.type) params.append('type', filters.type);
        if (filters.zone_id) params.append('zone_id', filters.zone_id);
        if (filters.zone) params.append('zone', filters.zone);
        if (filters.search) params.append('search', filters.search);
        if (filters.page !== undefined) params.append('page', filters.page.toString());
        if (filters.page_size !== undefined) params.append('page_size', filters.page_size.toString());
        if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
        
        const queryString = params.toString();
        // Use dedicated /api/v1/locations endpoint (location_management router)
        const locationsUrl = queryString ? `${API_ENDPOINTS.LOCATIONS}?${queryString}` : API_ENDPOINTS.LOCATIONS;
        
        const response = await api.get(locationsUrl);
        const data = response.data || {};
        const locationsList = Array.isArray(data.locations) ? data.locations : (Array.isArray(data.warehouse_locations) ? data.warehouse_locations : (Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : [])));
        
        return {
          locations: locationsList,
          items: locationsList, // Alias for compatibility
          total: data.total || data.total_count || locationsList.length
        };
      } catch {
        // Return empty data if endpoint doesn't exist (404) or other errors
        
        return {
          locations: [],
          items: [],
          total: 0
        };
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Fetches zones for a warehouse
 * 
 * Fetches warehouse zones which are organizational units within a warehouse
 * used for grouping locations and managing inventory flow. Zones help organize
 * warehouse space into logical sections for better inventory management.
 * 
 * @param {string} [warehouseId] - Optional warehouse ID to filter zones by warehouse
 * @returns {Object} React Query result with zones data
 * @property {Array} data - Array of zone records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useZones = (warehouseId) => {
  return useQuery({
    queryKey: ['zones', warehouseId],
    queryFn: async () => {
      try {
        const url = warehouseId 
          ? `${API_ENDPOINTS.WAREHOUSES}/zones?warehouse_id=${warehouseId}`
          : `${API_ENDPOINTS.WAREHOUSES}/zones`;
        const response = await api.get(url);
        const data = response.data || {};
        return Array.isArray(data.zones) ? data.zones : (Array.isArray(data) ? data : []);
      } catch {
        // Return empty array if endpoint doesn't exist (404) or other errors
        
        return [];
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Mutation hook to create a new location
 * 
 * Automatically invalidates locations query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (locationData) => {
      const response = await api.post(API_ENDPOINTS.LOCATIONS, locationData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

/**
 * Mutation hook to update an existing location
 * 
 * Automatically invalidates both the specific location and locations list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ locationId, data }) => {
      const response = await api.put(`${API_ENDPOINTS.LOCATIONS}/${locationId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

/**
 * Mutation hook to delete a location
 * 
 * Automatically invalidates locations query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (locationId) => {
      const response = await api.delete(`${API_ENDPOINTS.LOCATIONS}/${locationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

/**
 * Mutation hook to import locations from CSV
 * 
 * Automatically invalidates locations query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useImportLocations = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      const response = await api.post(`${API_ENDPOINTS.LOCATIONS}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

/**
 * Query hook to fetch location statistics
 * 
 * Provides comprehensive statistics about warehouse locations including:
 * - Total locations count
 * - Occupied locations count
 * - Occupancy rate percentage
 * - Critical locations count
 * 
 * Auto-refreshes every 30 seconds to keep data up-to-date for real-time monitoring.
 * 
 * @returns {Object} React Query result with location statistics
 * @property {Object} data - Location statistics object
 * @property {number} data.total_locations - Total number of locations
 * @property {number} data.locations_total - Alternative key for total locations
 * @property {number} data.locations_occupied - Number of occupied locations
 * @property {number} data.occupied - Alternative key for occupied locations
 * @property {number} data.occupancy_rate - Occupancy rate percentage (0-100)
 * @property {number} data.critical_locations - Number of critical locations requiring attention
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useLocationStats = () => {
  return useQuery({
    queryKey: ['locations', 'stats'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.LOCATIONS}/statistics/utilization`);
        return response.data || {};
      } catch {
        // Return empty stats if endpoint doesn't exist (404) or other errors
        
        return {
          total_locations: 0,
          locations_total: 0,
          locations_occupied: 0,
          occupied: 0,
          occupancy_rate: 0,
          critical_locations: 0
        };
      }
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    retry: false, // Don't retry on 404 errors
  });
};

